import React, { useState, useEffect, useRef, useMemo } from 'react';
import ActivateInterventionModal from '../components/ActivateInterventionModal';
import FilterSheet from '../components/FilterSheet';
import { SearchField, ActiveStrip, EmptyFiltered, QuickChip, SheetLabel, TimeQuick, ArchiveRow, OptRow, AdvancedToggle, TIME_PAST, timeRange, rangeYears, timeLabel, norm, defaultCustom } from '../components/FilterBar';
import api from '../api';
import { Zap, Link, MapPin, Users, ChevronDown, ChevronUp, ChevronRight, Play, Navigation, Pencil, Check, Plus, RotateCcw, Trash2, UserPlus, X, SlidersHorizontal, UserCheck, Hand, Camera, Image as ImageIcon, User, AlertTriangle, Search } from 'lucide-react';
import AttachmentViewer from '../components/AttachmentViewer';

const CLOSED_PAGE_SIZE = 10;

// Opcije filtera "Sudjelovanje" — ikone/boje prate kartice
// Status lokacije izveden iz vremena rada
function locStatusKey(loc) {
  if (loc.ended_at) return 'done';
  if (loc.started_at) return 'in_progress';
  return 'pending';
}

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = Math.round((Date.now() - d.getTime()) / 1000); // s; +past / -future
  const abs = Math.abs(diff);
  const fut = diff < 0;
  if (abs < 90) return 'upravo';
  const m = Math.round(abs / 60);
  if (m < 60) return fut ? `za ${m} min` : `prije ${m} min`;
  const h = Math.round(abs / 3600);
  if (h < 24) return fut ? `za ${h} h` : `prije ${h} h`;
  const days = Math.round(abs / 86400);
  if (days === 1) return fut ? 'sutra' : 'jučer';
  if (days < 7) return fut ? `za ${days} d` : `prije ${days} d`;
  return '';
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// datetime-local vrijednost (lokalno vrijeme) → ISO s vremenskom zonom, kao kod zadataka.
const toIso = (v) => (v ? new Date(v).toISOString() : null);

function formatForInput(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowForInput() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Avatar({ name, size = 28 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['#0984e3','#6c5ce7','#00b894','#e17055','#fd79a8','#fdcb6e','#74b9ff','#a29bfe'];
  const bg = colors[(name || '').charCodeAt(0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.36, fontWeight: 600, flexShrink: 0, letterSpacing: '-0.5px' }}>
      {initials}
    </div>
  );
}

// Fotografije lokacije — isto kao prilozi zadatka: pregled dodirom (AttachmentViewer, „natrag” zatvara),
// dodavanje/brisanje samo u „Uredi” kao nacrt koji se učitava na „Spremi” (s napretkom).
const PHOTO_MAX = 2; // po vrsti (prije / poslije)
const PHOTO_ACCEPT = 'image/jpeg,image/png';
const PHOTO_LABEL = { before: 'Prije', after: 'Poslije' };
const fileSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const PHOTO_MAX_PX = 2048;      // duža strana nakon smanjivanja
const PHOTO_KEEP_BYTES = 1.5 * 1024 * 1024;

// Smanji fotografiju prije slanja (kamera daje 3–8 MB, backend prima do 5 MB). Mala slika ostaje kakva jest;
// ako preglednik ne može dekodirati (npr. HEIC), šalje se original.
async function shrinkPhoto(file) {
  const src = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = src; });
    const w = img.naturalWidth, h = img.naturalHeight;
    const k = Math.min(1, PHOTO_MAX_PX / Math.max(w, h));
    if (k === 1 && file.size <= PHOTO_KEEP_BYTES) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * k); canvas.height = Math.round(h * k);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);   // PNG s prozirnošću → bijela pozadina
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);                    // EXIF rotaciju preglednik već primijeni
    const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch { return file; }
  finally { URL.revokeObjectURL(src); }
}

const photoThumb = { width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid #eef0f3', flexShrink: 0, display: 'block' };

function LocationPhotos({ images, locationName, draft, setDraft, upload, busy, onView }) {
  const editable = !!draft;
  const [shrinking, setShrinking] = useState(null);   // vrsta za koju se fotografija upravo smanjuje
  const onPick = async (e, type) => {
    const picked = e.target.files[0];
    e.target.value = '';
    if (!picked) return;
    setShrinking(type);
    const file = await shrinkPhoto(picked);
    setShrinking(null);
    setDraft(d => ({ ...d, add: [...d.add, { key: `${Date.now()}${file.name}`, file, type, preview: URL.createObjectURL(file) }] }));
  };
  const unpick = (f) => { URL.revokeObjectURL(f.preview); setDraft(d => ({ ...d, add: d.add.filter(x => x.key !== f.key) })); };
  if (!editable && images.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      {['before', 'after'].map(type => {
        const imgs = images.filter(i => i.type === type);
        const added = editable ? draft.add.filter(f => f.type === type) : [];
        if (!editable && imgs.length === 0) return null;
        const count = imgs.filter(i => !editable || !draft.remove.includes(i.id)).length + added.length;
        return (
          <div key={type}>
            <div className="adm-lbl">Fotografije {PHOTO_LABEL[type].toLowerCase()} · {count}/{PHOTO_MAX}</div>
            <div className="adm-card">
              {imgs.map((img, i) => {
                const gone = editable && draft.remove.includes(img.id);
                const att = { ...img, original_name: `${PHOTO_LABEL[type]} ${i + 1} · ${locationName}` };
                return (
                  <div key={img.id} className={`adm-it${gone ? ' is-removed' : ''}`}>
                    <div className="adm-main" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, cursor: gone ? 'default' : 'pointer' }} onClick={gone ? undefined : () => onView(att)}>
                      <img src={img.url} alt="" style={{ ...photoThumb, opacity: gone ? 0.45 : 1 }} />
                      <div style={{ minWidth: 0 }}>
                        <div className="adm-t soft" style={{ fontSize: 15.5 }}>{PHOTO_LABEL[type]} {i + 1}</div>
                        {gone ? <div className="adm-pending">bit će obrisana</div> : <div className="adm-s">{img.uploaded_at ? formatDateTime(img.uploaded_at) : 'Fotografija'}</div>}
                      </div>
                    </div>
                    {editable && (gone
                      ? <button type="button" className="adm-pbtn" onClick={() => setDraft(d => ({ ...d, remove: d.remove.filter(x => x !== img.id) }))}><RotateCcw size={14} /> Vrati</button>
                      : <button type="button" className="adm-x" title="Obriši fotografiju" disabled={busy} onClick={() => setDraft(d => ({ ...d, remove: [...d.remove, img.id] }))}><X size={18} /></button>)}
                  </div>
                );
              })}
              {added.map(f => (
                <div key={f.key} className="adm-it is-new">
                  <div className="adm-main" style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <img src={f.preview} alt="" style={photoThumb} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="adm-t soft" style={{ fontSize: 15.5 }}>Nova fotografija</div>
                      {upload?.key === f.key
                        ? <div className="tf-bar" style={{ marginTop: 6 }}><i style={{ width: `${upload.pct}%` }} /></div>
                        : <div className="adm-pending" style={{ color: '#3f8f6b', whiteSpace: 'normal' }}>novo · {fileSize(f.file.size)} · učitava se na „Spremi”</div>}
                    </div>
                  </div>
                  <button type="button" className="adm-x" title="Makni" disabled={busy} onClick={() => unpick(f)}><X size={18} /></button>
                </div>
              ))}
              {editable && shrinking === type && (
                <div className="adm-act" style={{ justifyContent: 'center', color: '#8a94a6', cursor: 'default' }}>Pripremam fotografiju…</div>
              )}
              {editable && shrinking !== type && (count < PHOTO_MAX ? (
                <div className="adm-act" style={{ padding: 0, gap: 0, cursor: 'default' }}>
                  {/* Android za image-only accept nudi samo galeriju — kamera ide preko capture */}
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 8px', cursor: 'pointer' }}>
                    <Camera size={17} /> Slikaj
                    <input type="file" accept={PHOTO_ACCEPT} capture="environment" style={{ display: 'none' }} onChange={e => onPick(e, type)} disabled={busy} />
                  </label>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 8px', cursor: 'pointer', borderLeft: '1px solid #eef0f4' }}>
                    <ImageIcon size={17} /> Iz galerije
                    <input type="file" accept={PHOTO_ACCEPT} style={{ display: 'none' }} onChange={e => onPick(e, type)} disabled={busy} />
                  </label>
                </div>
              ) : (
                <div className="adm-act" style={{ justifyContent: 'center', color: '#9aa1ac', cursor: 'default' }}>Najviše {PHOTO_MAX} fotografije</div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── FAB Stack ────────────────────────────────────────────────────────────────
function FabStack({ mainButtons, filterButton, hasActiveFilters, onClearFilters, hidden, active }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (hidden || active === false) {
      setVisible(false);
    } else {
      timerRef.current = setTimeout(() => setVisible(true), 10);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [hidden, active]);

  const fabStyle = {
    width: 48, height: 48, borderRadius: 12, border: 'none',
    color: 'white', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(27,61,143,0.35)',
    transition: 'background 0.15s, transform 0.15s, box-shadow 0.15s',
  };

  return (
    <div style={{
      position: 'fixed', right: 16, bottom: 'calc(100px + env(safe-area-inset-bottom))',
      display: 'flex', flexDirection: 'column', gap: 10, zIndex: 150,
      opacity: visible ? 1 : 0,
      transform: visible ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(8px)',
      transition: 'opacity 0.25s ease, transform 0.25s ease',
      pointerEvents: visible ? 'auto' : 'none',
    }}>
      {mainButtons.map((btn, i) => (
        <button key={i} onClick={btn.onClick} title={btn.title}
          style={{ ...fabStyle, background: btn.active ? '#d63031' : '#1a2a4a' }}
        >
          {btn.icon}
        </button>
      ))}

      <div style={{ position: 'relative', width: 48, height: 48 }}>
        {hasActiveFilters && (
          <button onClick={onClearFilters} title="Očisti filtere"
            style={{ ...fabStyle, background: '#d63031', position: 'absolute', right: 58, top: 0, animation: 'fabIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          >
            <X size={20} />
          </button>
        )}
        <button onClick={filterButton.onClick} title={filterButton.title}
          style={{ ...fabStyle, background: filterButton.active ? '#d63031' : '#1a2a4a', position: 'absolute', right: 0, top: 0 }}
        >
          {filterButton.icon}
          {filterButton.badge > 0 && <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 20, height: 20, borderRadius: 10, background: '#b4552d', color: '#fff', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', border: '2px solid #fff' }}>{filterButton.badge}</span>}
        </button>
      </div>
    </div>
  );
}

function FabStackWrapper({ mainButtons, filterButton, hasActiveFilters, onClearFilters, hidden }) {
  const [shouldRender, setShouldRender] = useState(!hidden);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!hidden) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setShouldRender(true);
    } else {
      timerRef.current = setTimeout(() => setShouldRender(false), 280);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [hidden]);

  if (!shouldRender) return null;

  return (
    <FabStack
      mainButtons={mainButtons}
      filterButton={filterButton}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      hidden={hidden}
    />
  );
}

// ─── Operativni prikaz (prijedlog 2) ──────────────────────────────────────────
// Aktivna intervencija = crveno zaglavlje (isto kao kartica na Početnoj), ispod
// na bijelom lokacije kao vremenska crta. Zatvorene = kompaktni redovi po mjesecima.

const LOC_HUE = { pending: '#8a94a6', in_progress: '#3a6ea3', done: '#3f8f6b' };
const ACTIVE_RED = '#9c3e34';
const CLOSED_SLATE = '#5b6472';
const MONTHS_SHORT = ['sij', 'velj', 'ožu', 'tra', 'svi', 'lip', 'srp', 'kol', 'ruj', 'lis', 'stu', 'pro'];

const hm = (s) => new Date(s).toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
const dm = (s) => { const d = new Date(s); return `${d.getDate()}. ${d.getMonth() + 1}.`; };
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

// h:mm za veliki brojač u zaglavlju
function formatClock(ms) {
  const mins = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(mins / 60);
  if (h >= 24) return `${Math.floor(h / 24)} d ${h % 24} h`;
  return `${h}:${String(mins % 60).padStart(2, '0')}`;
}

// "2 h 25 min" za retke
function formatSpan(ms) {
  const mins = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) return `${Math.floor(h / 24)} d ${h % 24} h`;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

// 1 član, 2–4 člana, 5+ članova (11–14 → članova)
function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

const shortName = (full) => {
  const parts = (full || '').trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : (full || '');
};

function useNow(enabled) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
}

// Lokacije kronološki: započete po vremenu početka, one koje čekaju na kraju
function sortLocations(list) {
  return [...list].sort((a, b) => {
    if (!a.started_at && !b.started_at) return (a.id || 0) - (b.id || 0);
    if (!a.started_at) return 1;
    if (!b.started_at) return -1;
    return new Date(a.started_at) - new Date(b.started_at);
  });
}

const smallBtn = { padding: '4px 10px', fontSize: 13.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' };
const outlineBtn = { ...smallBtn, background: '#fff', color: '#1a2a4a', border: '1px solid #dfe3ea' };
const sectionLabel = { fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#8a94a6', display: 'flex', alignItems: 'center', gap: 6 };

function LocationRow({ location, canManage, showActions, onUpdate, onDelete, isFirst, isLast, interventionStart, now }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: location.name, description: location.description || '', lat: location.lat || '', lng: location.lng || '', started_at: formatForInput(location.started_at), ended_at: formatForInput(location.ended_at) });
  const [gpsLoading, setGpsLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [photoDraft, setPhotoDraft] = useState(null);   // { add: [{ key, file, type, preview }], remove: [id] } dok se uređuje
  const [upload, setUpload] = useState(null);           // { i, n, pct, key } dok se fotografija učitava
  const [busy, setBusy] = useState(false);
  const [viewImg, setViewImg] = useState(null);
  const loadImages = () => api.get(`/interventions/locations/${location.id}/images`).then(r => setImages(r.data)).catch(() => {});
  useEffect(() => { if (expanded || editing) loadImages(); }, [expanded, editing, location.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dropDraft = () => { photoDraft?.add.forEach(f => URL.revokeObjectURL(f.preview)); setPhotoDraft(null); };
  const startEdit = () => { setPhotoDraft({ add: [], remove: [] }); setEditing(true); };
  const cancelEdit = () => { dropDraft(); setEditing(false); };
  const getGPS = () => {
    if (!navigator.geolocation) return alert('GPS nije dostupan.');
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(pos => { setForm(p => ({ ...p, lat: pos.coords.latitude.toFixed(7), lng: pos.coords.longitude.toFixed(7) })); setGpsLoading(false); }, () => { alert('Nije moguće dohvatiti lokaciju.'); setGpsLoading(false); });
  };
  const handleSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put(`/interventions/locations/${location.id}`, { ...form, lat: form.lat || null, lng: form.lng || null, started_at: toIso(form.started_at), ended_at: toIso(form.ended_at) });
      const d = photoDraft || { add: [], remove: [] };
      for (const id of d.remove) await api.delete(`/interventions/images/${id}`);
      for (let i = 0; i < d.add.length; i++) {
        const f = d.add[i];
        const fd = new FormData();
        fd.append('image', f.file);
        fd.append('type', f.type);
        await api.post(`/interventions/locations/${location.id}/images`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => e.total && setUpload({ i: i + 1, n: d.add.length, key: f.key, pct: Math.round((e.loaded / e.total) * 100) }),
        });
        URL.revokeObjectURL(f.preview);
        setPhotoDraft(p => p && { ...p, add: p.add.filter(x => x.key !== f.key) });
      }
      setPhotoDraft(null); setEditing(false); onUpdate();   // efekt na editing ponovno dohvati fotografije
    } catch (err) {
      alert(err.response?.data?.error || 'Greška.');
      loadImages();
    } finally { setBusy(false); setUpload(null); }
  };
  const handleQuick = async (patch) => {
    try { await api.put(`/interventions/locations/${location.id}`, patch); onUpdate(); }
    catch (err) { alert(err.response?.data?.error || 'Greška.'); }
  };
  const stKey = locStatusKey(location);
  const hue = LOC_HUE[stKey];

  if (editing) {
    return (
      <div style={{ padding: '10px 0', borderTop: '1px solid #f1f3f6' }}>
        <div className="form-group" style={{ marginBottom: 8 }}><label style={{ fontSize: 14 }}>Naziv</label><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} maxLength={100} /></div>
        <div className="form-group" style={{ marginBottom: 8 }}><label style={{ fontSize: 14 }}>Opis</label><textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} maxLength={500} style={{ width: '100%', padding: '6px 10px', border: '1px solid #dfe6e9', borderRadius: 6, fontSize: 15, resize: 'vertical', minHeight: 60 }} /></div>
        <div className="form-row" style={{ marginBottom: 8 }}>
          <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: 14 }}>Početak rada</label><input type="datetime-local" value={form.started_at} onChange={e => setForm(p => ({ ...p, started_at: e.target.value }))} /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: 14 }}>Kraj rada</label><input type="datetime-local" value={form.ended_at} onChange={e => setForm(p => ({ ...p, ended_at: e.target.value }))} /></div>
        </div>
        <div className="form-row" style={{ marginBottom: 8 }}>
          <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: 14 }}>Latitude</label><input value={form.lat} onChange={e => setForm(p => ({ ...p, lat: e.target.value }))} placeholder="npr. 45.8150" /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: 14 }}>Longitude</label><input value={form.lng} onChange={e => setForm(p => ({ ...p, lng: e.target.value }))} placeholder="npr. 15.9819" /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button style={outlineBtn} onClick={getGPS} disabled={gpsLoading}><Navigation size={12} /> {gpsLoading ? 'Dohvaćam...' : 'GPS'}</button>
          <button style={outlineBtn} onClick={cancelEdit} disabled={busy}><X size={12} /> Odustani</button>
          <button style={{ ...smallBtn, background: '#1a2a4a', color: '#fff', border: 'none', opacity: busy ? 0.7 : 1 }} onClick={handleSave} disabled={busy}>
            <Check size={12} /> {busy ? (upload ? `Učitavam ${upload.i}/${upload.n} · ${upload.pct}%` : 'Spremam…') : 'Spremi'}
          </button>
        </div>
        {canManage && <LocationPhotos images={images} locationName={location.name} draft={photoDraft} setDraft={setPhotoDraft} upload={upload} busy={busy} onView={setViewImg} />}
        {viewImg && <AttachmentViewer attachment={viewImg} onClose={() => setViewImg(null)} />}
      </div>
    );
  }

  let sub;
  if (stKey === 'done') sub = <>završeno u <span className="tab">{hm(location.ended_at)}</span>{location.started_at && <> · {formatSpan(new Date(location.ended_at) - new Date(location.started_at))}</>}</>;
  else if (stKey === 'in_progress') sub = <span style={{ color: hue, fontWeight: 600 }}>u tijeku · {formatSpan(now - new Date(location.started_at))}</span>;
  else sub = 'čeka';

  const showDate = location.started_at && interventionStart && !sameDay(location.started_at, interventionStart);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '42px 18px 1fr auto', columnGap: 8 }}>
      <div className="tab" style={{ fontSize: 13, color: '#8a94a6', textAlign: 'right', paddingTop: 8, lineHeight: 1.2 }} title={location.started_at ? formatDateTime(location.started_at) : 'Nije započeto'}>
        {location.started_at ? hm(location.started_at) : '—'}
        {showDate && <div style={{ fontSize: 11.5 }}>{dm(location.started_at)}</div>}
      </div>
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        {!(isFirst && isLast) && (
          <div style={{ position: 'absolute', left: 'calc(50% - 1px)', width: 2, background: '#e5e8ee', top: isFirst ? 14 : 0, ...(isLast ? { height: 14 } : { bottom: 0 }) }} />
        )}
        <div style={{ position: 'relative', marginTop: 8, width: 12, height: 12, borderRadius: '50%', boxSizing: 'border-box', border: `2.5px solid ${hue}`, background: stKey === 'done' ? hue : '#fff' }} />
      </div>
      <div onClick={() => setExpanded(v => !v)} style={{ minWidth: 0, padding: '5px 0 9px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 16, fontWeight: 700, color: '#1a2130' }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{location.name}</span>
          <ChevronDown size={14} color="#b2bec3" style={{ flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none' }} />
        </div>
        <div style={{ fontSize: 14, color: '#6b7587' }}>{sub}</div>
      </div>
      <div style={{ paddingTop: 6 }}>
        {showActions && canManage && stKey === 'pending' && (
          <button style={{ ...smallBtn, background: '#3a6ea3', color: '#fff', border: 'none' }} onClick={() => handleQuick({ started_at: new Date().toISOString() })}><Play size={11} /> Započni</button>
        )}
        {showActions && canManage && stKey === 'in_progress' && (
          <button style={{ ...smallBtn, background: '#3f8f6b', color: '#fff', border: 'none' }} onClick={() => handleQuick({ ended_at: new Date().toISOString() })}><Check size={11} /> Završi</button>
        )}
      </div>

      {expanded && (
        <div style={{ gridColumn: '3 / 5', paddingBottom: 10, animation: 'slideDown 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          {(location.started_at || location.ended_at) && (
            <div className="tab" style={{ fontSize: 14, color: '#8a94a6' }}>
              {location.started_at ? formatDateTime(location.started_at) : '—'}{location.ended_at && ` → ${formatDateTime(location.ended_at)}`}
            </div>
          )}
          {location.description && <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.5, marginTop: 4 }}>{location.description}</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {location.lat && location.lng && (
              <a href={`https://maps.google.com/?q=${location.lat},${location.lng}`} target="_blank" rel="noreferrer" style={{ ...outlineBtn, textDecoration: 'none' }}>
                <Navigation size={11} /> Karta
              </a>
            )}
            {canManage && <button style={outlineBtn} onClick={startEdit}><Pencil size={11} /> Uredi</button>}
            {canManage && <button style={{ ...outlineBtn, color: '#ac463b' }} onClick={() => onDelete(location.id)}><Trash2 size={11} /> Obriši</button>}
          </div>
          <LocationPhotos images={images} locationName={location.name} onView={setViewImg} />
          {canManage && images.length === 0 && <div style={{ fontSize: 13.5, color: '#9aa1ac', marginTop: 8 }}>Fotografije prije/poslije dodaješ u „Uredi”.</div>}
        </div>
      )}
      {viewImg && <AttachmentViewer attachment={viewImg} onClose={() => setViewImg(null)} />}
    </div>
  );
}

function InterventionCard({ intervention, user, onUpdate, defaultExpanded, onCollapse, focusNonce }) {
  const [expanded, setExpanded] = useState(defaultExpanded || false);
  const cardRef = useRef(null);
  // Otvaranje izvana (Početna, link iz WhatsAppa) — stranica ostaje mountana, pa defaultExpanded nije dovoljan
  useEffect(() => {
    if (!focusNonce) return;
    setExpanded(true);
    const t = setTimeout(() => {
      if (!cardRef.current) return;
      const y = cardRef.current.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(t);
  }, [focusNonce]);
  const [editing, setEditing] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: '', started_at: '' });
  const [userSearch, setUserSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ title: intervention.title, notes: intervention.notes || '', started_at: formatForInput(intervention.started_at), ended_at: formatForInput(intervention.ended_at), required_people: intervention.required_people || 1 });
  const [stagedParticipantAdd, setStagedParticipantAdd] = useState([]);
  const [stagedParticipantRemove, setStagedParticipantRemove] = useState([]);
  const [stagedVolunteerDecline, setStagedVolunteerDecline] = useState([]);
  const [stagedLocationAdd, setStagedLocationAdd] = useState([]);
  const [stagedLocationDelete, setStagedLocationDelete] = useState([]);
  const [confirmUnvolunteer, setConfirmUnvolunteer] = useState(false);   // inline potvrda odjave dok se čeka prihvat
  const [openVolunteer, setOpenVolunteer] = useState(null);               // zainteresirani kojem su otvoreni Dodijeli/Odbij

  const resetStagedState = () => {
    setStagedParticipantAdd([]);
    setStagedParticipantRemove([]);
    setStagedVolunteerDecline([]);
    setStagedLocationAdd([]);
    setStagedLocationDelete([]);
  };

  const canManage = ['admin', 'manager'].includes(user.role);
  const isVolunteer = intervention.volunteers?.some(v => v.email === user.email);
  const isParticipant = intervention.participants?.some(p => p.email === user.email);
  const participantCount = intervention.participants?.length || 0;
  const requiredPeople = intervention.required_people || 1;
  const isActive = intervention.status === 'active';
  const now = useNow(isActive);

  const toggleExpand = () => {
    if (expanded && onCollapse) { onCollapse(); return; }
    setExpanded(v => !v);
  };

  const handleRemoveVolunteer = async (email) => {
    if (editing) {
      setStagedVolunteerDecline(prev => [...prev, email]);
    } else {
      try {
        await api.delete(`/interventions/${intervention.id}/volunteers/${encodeURIComponent(email)}`);
        onUpdate();
      } catch (err) { alert(err.response?.data?.error || 'Greška.'); }
    }
  };

  useEffect(() => { if (expanded && canManage) api.get('/users').then(res => setAllUsers(res.data)); }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    try {
      await api.put(`/interventions/${intervention.id}`, { ...form, started_at: toIso(form.started_at), ended_at: toIso(form.ended_at) });
      await Promise.all([
        ...stagedParticipantAdd.map(x => api.post(`/interventions/${intervention.id}/participants`, { email: x.email, action: 'add' })),
        ...stagedParticipantRemove.map(email => api.post(`/interventions/${intervention.id}/participants`, { email, action: 'remove' })),
        ...stagedVolunteerDecline.map(email => api.delete(`/interventions/${intervention.id}/volunteers/${encodeURIComponent(email)}`)),
        ...stagedLocationDelete.map(id => api.delete(`/interventions/locations/${id}`)),
        ...stagedLocationAdd.map(loc => api.post(`/interventions/${intervention.id}/locations`, { name: loc.name, started_at: toIso(loc.started_at) })),
      ]);
      setEditing(false);
      resetStagedState();
      onUpdate();
    } catch (err) { alert(err.response?.data?.error || 'Greška.'); }
  };
  const handleClose = async () => { if (!window.confirm('Zatvoriti intervenciju?')) return; await api.put(`/interventions/${intervention.id}/close`); onUpdate(); };
  const handleReopen = async () => { await api.put(`/interventions/${intervention.id}/reopen`); onUpdate(); };
  const handleDelete = async () => { if (!window.confirm('Obrisati intervenciju?')) return; await api.delete(`/interventions/${intervention.id}`); onUpdate(); };
  const handleVolunteer = async () => { if (isVolunteer) { await api.delete(`/interventions/${intervention.id}/volunteer`); } else { await api.post(`/interventions/${intervention.id}/volunteer`); } onUpdate(); };
  const handleParticipant = async (email, action) => {
    const userInfo = allUsers.find(u => u.email === email) || intervention.volunteers?.find(v => v.email === email);
    if (editing) {
      if (action === 'add') {
        setStagedParticipantAdd(prev => [...prev.filter(x => x.email !== email), { email, full_name: userInfo?.full_name || email }]);
        setStagedParticipantRemove(prev => prev.filter(e => e !== email));
      } else {
        if (stagedParticipantAdd.some(x => x.email === email)) {
          setStagedParticipantAdd(prev => prev.filter(x => x.email !== email));
        } else {
          setStagedParticipantRemove(prev => [...prev, email]);
        }
      }
    } else {
      try {
        await api.post(`/interventions/${intervention.id}/participants`, { email, action });
        onUpdate();
      } catch (err) { alert(err.response?.data?.error || 'Greška.'); }
    }
  };
  const handleAddLocation = async () => {
    if (!newLocation.name.trim()) return alert('Naziv lokacije je obavezan.');
    if (editing) {
      setStagedLocationAdd(prev => [...prev, { name: newLocation.name, started_at: newLocation.started_at || null, _tempId: Date.now() }]);
    } else {
      // Izvan uređivanja lokacija se sprema odmah (prije se samo stageala i nikad nije spremila)
      try { await api.post(`/interventions/${intervention.id}/locations`, { name: newLocation.name, started_at: toIso(newLocation.started_at) }); onUpdate(); }
      catch (err) { return alert(err.response?.data?.error || 'Greška.'); }
    }
    setNewLocation({ name: '', started_at: '' });
    setShowAddLocation(false);
  };
  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Obrisati lokaciju?')) return;
    if (editing) { setStagedLocationDelete(prev => [...prev, locationId]); return; }
    try { await api.delete(`/interventions/locations/${locationId}`); onUpdate(); }
    catch (err) { alert(err.response?.data?.error || 'Greška.'); }
  };
  const handleShare = async () => {
    const url = `${window.location.origin}/?intervention=${intervention.id}`;
    const text = `*INTERVENCIJA: ${intervention.title} (${participantCount}/${requiredPeople})*\n${url}`;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { const el = document.createElement('textarea'); el.value = text; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const participantEmails = intervention.participants?.map(p => p.email) || [];
  const displayedParticipants = (intervention.participants || []).filter(p => !stagedParticipantRemove.includes(p.email));
  const displayedVolunteers = (intervention.volunteers || []).filter(v =>
    !participantEmails.includes(v.email) &&
    !stagedParticipantAdd.some(x => x.email === v.email) &&
    !stagedVolunteerDecline.includes(v.email)
  );
  const displayedLocations = sortLocations((intervention.locations || []).filter(loc => loc && !stagedLocationDelete.includes(loc.id)));
  const filteredUsers = allUsers
    .filter(u => (!participantEmails.includes(u.email) || stagedParticipantRemove.includes(u.email)) && !stagedParticipantAdd.some(x => x.email === u.email))
    .filter(u => { const q = userSearch.toLowerCase(); return (u.full_name || '').toLowerCase().includes(q) || u.email.toLowerCase().includes(q); });

  const locTotal = displayedLocations.length + stagedLocationAdd.length;
  const locDone = displayedLocations.filter(l => locStatusKey(l) === 'done').length;
  const canEditLocations = canManage && (isActive || editing);
  const showLocations = locTotal > 0 || (expanded && canEditLocations);
  const headBg = isActive ? ACTIVE_RED : CLOSED_SLATE;
  const soft = isActive ? '#f3c9c2' : '#d5dae2';
  const durationMs = isActive
    ? now - new Date(intervention.started_at).getTime()
    : (intervention.ended_at ? new Date(intervention.ended_at) - new Date(intervention.started_at) : null);
  const startedRel = relativeTime(intervention.started_at);
  const fillColor = participantCount < requiredPeople ? '#a8781f' : '#3f8f6b';
  const stats = [
    durationMs != null && { v: formatClock(durationMs), l: isActive ? 'traje' : 'trajala' },
    { v: `${participantCount}/${requiredPeople}`, l: 'ljudi' },
    displayedLocations.length > 0 && { v: `${locDone}/${displayedLocations.length}`, l: 'lokacije' },
  ].filter(Boolean);

  return (
    <div ref={cardRef} style={{
      marginBottom: 14, background: '#fff', borderRadius: 14, overflow: 'hidden',
      boxShadow: isActive ? '0 6px 18px rgba(156,62,52,0.18)' : '0 1px 2px rgba(16,24,40,.05), 0 4px 14px rgba(16,24,40,.08)',
    }}>
      {/* ── Zaglavlje ── */}
      <div onClick={toggleExpand} style={{ background: headBg, color: '#fff', padding: '12px 14px 13px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: soft }}>
          {isActive ? (
            <div style={{ position: 'relative', width: 7, height: 7, flexShrink: 0 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
              <div style={{ position: 'absolute', inset: -5, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.45)', animation: 'attentionPulse 1.6s infinite' }} />
            </div>
          ) : <Check size={12} strokeWidth={3} />}
          <span style={{ flex: 1, minWidth: 0 }}>{isActive ? 'Aktivna intervencija' : `Zatvorena · ${dm(intervention.started_at)}`}</span>
          {isParticipant && <span title="Sudjeluješ na ovoj intervenciji" style={{ display: 'inline-flex' }}><UserCheck size={15} color="#fff" strokeWidth={2.2} /></span>}
          {isVolunteer && !isParticipant && <span title="Prijavljen/a za ovu intervenciju" style={{ display: 'inline-flex' }}><Hand size={15} color="#fff" strokeWidth={2.2} /></span>}
          <button onClick={e => { e.stopPropagation(); handleShare(); }} title="Kopiraj link" style={{ background: copied ? 'rgba(255,255,255,0.22)' : 'none', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 5px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            {copied ? <Check size={15} /> : <Link size={15} />}
          </button>
          <ChevronDown size={17} color="#fff" style={{ transition: 'transform 0.25s', transform: expanded ? 'rotate(180deg)' : 'none' }} />
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, margin: '5px 0 2px', overflowWrap: 'break-word' }}>{intervention.title}</div>
        <div className="tab" style={{ fontSize: 14, color: soft }}>
          {isActive
            ? <>Početak {formatDateTime(intervention.started_at)}{startedRel && ` · ${startedRel}`}</>
            : <>{formatDateTime(intervention.started_at)}{intervention.ended_at && ` → ${sameDay(intervention.started_at, intervention.ended_at) ? hm(intervention.ended_at) : formatDateTime(intervention.ended_at)}`}</>}
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 10, alignItems: 'flex-end' }}>
          {stats.map(s => (
            <div key={s.l} style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: soft }}>
              <div className="tab" style={{ fontSize: 25, fontWeight: 700, color: '#fff', letterSpacing: 0, lineHeight: 1.05 }}>{s.v}</div>
              {s.l}
            </div>
          ))}
        </div>

        {expanded && (isActive || canManage) && !editing && (
          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', cursor: 'default' }}>
            {isActive && isParticipant && (
              <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.16)', fontSize: 15, fontWeight: 700 }}>
                <UserCheck size={15} /> Sudjeluješ
              </span>
            )}
            {isActive && !isParticipant && (confirmUnvolunteer ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>Odjaviti se?</span>
                <button onClick={() => { setConfirmUnvolunteer(false); handleVolunteer(); }} style={{ padding: '8px 14px', borderRadius: 9, border: 'none', background: '#fff', color: headBg, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Odjavi se</button>
                <button onClick={() => setConfirmUnvolunteer(false)} style={{ padding: '8px 14px', borderRadius: 9, border: 'none', background: 'rgba(255,255,255,0.16)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Odustani</button>
              </div>
            ) : (
              <button onClick={() => { if (isVolunteer) setConfirmUnvolunteer(true); else handleVolunteer(); }} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 12px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700, background: isVolunteer ? 'rgba(255,255,255,0.16)' : '#fff', color: isVolunteer ? '#fff' : headBg }}>
                {isVolunteer ? <><X size={15} /> Prijavljen/a · Odjavi se</> : <><Check size={15} /> Javi se</>}
              </button>
            ))}
            {canManage && (
              <button onClick={() => setEditing(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, background: 'rgba(255,255,255,0.16)', color: '#fff' }}>
                <Pencil size={14} /> Uredi
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Uređivanje ── */}
      {expanded && editing && (
        <div style={{ padding: '14px 14px 2px', background: '#fbfbfc', borderBottom: '1px solid #f0f2f5' }}>
          <div className="form-group"><label>Naziv *</label><input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} maxLength={100} /></div>
          <div className="form-row">
            <div className="form-group"><label>Početak</label><input type="datetime-local" value={form.started_at} onChange={e => setForm(p => ({ ...p, started_at: e.target.value }))} /></div>
            <div className="form-group"><label>Kraj</label><input type="datetime-local" value={form.ended_at} onChange={e => setForm(p => ({ ...p, ended_at: e.target.value }))} /></div>
          </div>
          <div className="form-group"><label>Potreban broj ljudi</label><input type="number" min="1" value={form.required_people} onChange={e => setForm(p => ({ ...p, required_people: e.target.value }))} /></div>
          <div className="form-group"><label>Napomene</label><textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} maxLength={2000} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dfe6e9', borderRadius: 8, fontSize: 17, resize: 'vertical', minHeight: 80 }} /></div>
        </div>
      )}

      {/* ── Lokacije: vremenska crta ── */}
      {showLocations && (
        <div style={{ padding: expanded ? '12px 14px 6px' : '8px 14px 6px' }}>
          {expanded && (
            <div style={{ ...sectionLabel, marginBottom: 6 }}>
              <MapPin size={12} /> Lokacije
              {canEditLocations && !showAddLocation && (
                <button onClick={() => { setNewLocation({ name: '', started_at: nowForInput() }); setShowAddLocation(true); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#3a6ea3', fontSize: 14, fontWeight: 700, letterSpacing: 0, textTransform: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0 }}>
                  <Plus size={13} /> Dodaj
                </button>
              )}
            </div>
          )}
          {displayedLocations.map((loc, idx) => (
            <LocationRow
              key={loc.id}
              location={loc}
              canManage={canEditLocations}
              showActions={expanded}
              onUpdate={onUpdate}
              onDelete={handleDeleteLocation}
              isFirst={idx === 0}
              isLast={idx === displayedLocations.length - 1}
              interventionStart={intervention.started_at}
              now={now}
            />
          ))}
          {editing && stagedLocationAdd.map(loc => (
            <div key={loc._tempId} style={{ background: '#eef4fb', border: '1px dashed #9dbbdc', borderRadius: 10, padding: '8px 12px', margin: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapPin size={13} color="#3a6ea3" />
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#1a2130' }}>{loc.name}</span>
              {loc.started_at && <span className="tab" style={{ fontSize: 13, color: '#6b7587' }}>{formatDateTime(loc.started_at)}</span>}
              <span style={{ fontSize: 13, color: '#3a6ea3', fontWeight: 700 }}>Nova</span>
              <button onClick={() => setStagedLocationAdd(prev => prev.filter(l => l._tempId !== loc._tempId))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7587', display: 'flex', alignItems: 'center' }}><X size={14} /></button>
            </div>
          ))}
          {expanded && showAddLocation && (
            <div style={{ background: '#fbfbfc', borderRadius: 10, padding: 12, border: '1px solid #eef0f3', margin: '6px 0 8px' }}>
              <input value={newLocation.name} onChange={e => setNewLocation(p => ({ ...p, name: e.target.value }))} placeholder="Naziv lokacije..." maxLength={100} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dfe6e9', borderRadius: 8, fontSize: 17, marginBottom: 8 }} autoFocus />
              <div className="form-group" style={{ marginBottom: 8 }}><label style={{ fontSize: 14 }}>Početak rada</label><input type="datetime-local" value={newLocation.started_at} onChange={e => setNewLocation(p => ({ ...p, started_at: e.target.value }))} /></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={outlineBtn} onClick={() => setShowAddLocation(false)}><X size={12} /> Odustani</button>
                <button style={{ ...smallBtn, background: '#1a2a4a', color: '#fff', border: 'none' }} onClick={handleAddLocation}><Check size={12} /> Dodaj</button>
              </div>
            </div>
          )}
          {expanded && locTotal === 0 && !showAddLocation && (
            <div style={{ fontSize: 14.5, color: '#9aa1ac', padding: '2px 0 8px' }}>Još nema lokacija.</div>
          )}
        </div>
      )}

      {expanded && (
        <>
          {/* ── Sudionici ── */}
          <div style={{ padding: '12px 14px', borderTop: '1px solid #f0f2f5' }}>
            <div style={{ ...sectionLabel, marginBottom: 8 }}>
              <Users size={12} /> Sudionici
              <span className="tab" style={{ marginLeft: 'auto', letterSpacing: 0, textTransform: 'none', fontSize: 14, color: fillColor }}>{participantCount} od {requiredPeople}</span>
            </div>
            <div style={{ height: 5, borderRadius: 3, background: '#eef0f4', overflow: 'hidden', marginBottom: 10 }}>
              <div style={{ height: '100%', width: `${Math.min(100, (participantCount / requiredPeople) * 100)}%`, background: fillColor, borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {displayedParticipants.map(p => (
                <span key={p.email} title={p.full_name || p.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f4f6f9', borderRadius: 999, padding: '3px 10px 3px 3px', fontSize: 14.5, fontWeight: 600, color: '#1a2130' }}>
                  <Avatar name={p.full_name || p.email} size={24} />
                  {shortName(p.full_name || p.email)}
                  {canManage && editing && (
                    <button onClick={() => handleParticipant(p.email, 'remove')} title="Ukloni" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ac463b', display: 'flex', padding: 0, marginLeft: 2 }}><X size={13} /></button>
                  )}
                </span>
              ))}
              {editing && stagedParticipantAdd.map(x => (
                <span key={x.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef4fb', border: '1px dashed #9dbbdc', borderRadius: 999, padding: '2px 9px 2px 2px', fontSize: 14.5, fontWeight: 600, color: '#1a2130' }}>
                  <Avatar name={x.full_name || x.email} size={24} />
                  {shortName(x.full_name || x.email)}
                  <button onClick={() => handleParticipant(x.email, 'remove')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7587', display: 'flex', padding: 0 }}><X size={13} /></button>
                </span>
              ))}
              {canManage && displayedVolunteers.map(v => (
                <button key={v.email} onClick={() => setOpenVolunteer(o => o === v.email ? null : v.email)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: openVolunteer === v.email ? '#f3e6c4' : '#fbf5e6', border: 'none', borderRadius: 999, padding: '3px 10px 3px 3px', fontSize: 14.5, fontWeight: 600, color: '#7a5a15', cursor: 'pointer' }}>
                  <Avatar name={v.full_name || v.email} size={24} />
                  {shortName(v.full_name || v.email)} · javio/la se
                </button>
              ))}
              {displayedParticipants.length === 0 && !(editing && stagedParticipantAdd.length) && !(canManage && displayedVolunteers.length) && (
                <span style={{ fontSize: 14.5, color: '#9aa1ac' }}>Još nema sudionika.</span>
              )}
            </div>
            {canManage && openVolunteer && displayedVolunteers.some(v => v.email === openVolunteer) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 10px', background: '#fbf5e6', borderRadius: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: 1, fontSize: 14.5, color: '#7a5a15', fontWeight: 600, minWidth: 120 }}>
                  {(displayedVolunteers.find(v => v.email === openVolunteer)?.full_name) || openVolunteer}
                </span>
                <button style={{ ...smallBtn, background: '#3f8f6b', color: '#fff', border: 'none' }} onClick={() => { handleParticipant(openVolunteer, 'add'); setOpenVolunteer(null); }}><UserPlus size={12} /> Dodijeli</button>
                <button style={{ ...outlineBtn, color: '#ac463b' }} onClick={() => { handleRemoveVolunteer(openVolunteer); setOpenVolunteer(null); }}><X size={12} /> Odbij</button>
              </div>
            )}
            {((canManage && isActive) || editing) && (
              <div style={{ marginTop: 10 }}>
                <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Pretraži i dodaj sudionika..." style={{ width: '100%', padding: '8px 12px', border: '1px solid #dfe6e9', borderRadius: 8, fontSize: 17, marginBottom: 4 }} />
                {userSearch && (
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #dfe6e9', borderRadius: 8 }}>
                    {filteredUsers.length === 0 ? <div style={{ padding: '8px 12px', color: '#b2bec3', fontSize: 15 }}>Nema rezultata.</div> : filteredUsers.map(u => (
                      <div key={u.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f5f6fa' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Avatar name={u.full_name || u.email} size={24} /><span style={{ fontSize: 15 }}>{u.full_name || u.email}</span></div>
                        <button style={outlineBtn} onClick={() => { handleParticipant(u.email, 'add'); setUserSearch(''); }}><UserPlus size={12} /> Dodijeli</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Napomene ── */}
          {!editing && intervention.notes && (
            <div style={{ padding: '12px 14px', borderTop: '1px solid #f0f2f5' }}>
              <div style={{ ...sectionLabel, marginBottom: 6 }}>Napomene</div>
              <div style={{ fontSize: 16, color: '#2d3436', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{intervention.notes}</div>
            </div>
          )}

          {/* ── Upravljanje ── */}
          {canManage && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px 14px', borderTop: '1px solid #f0f2f5', background: '#fbfbfc' }}>
              {editing ? (
                <>
                  <button style={{ ...outlineBtn, padding: '9px 14px', fontSize: 15 }} onClick={() => { setEditing(false); resetStagedState(); }}><X size={14} /> Odustani</button>
                  <button style={{ ...smallBtn, padding: '9px 14px', fontSize: 15, flex: 1, justifyContent: 'center', background: '#1a2a4a', color: '#fff', border: 'none' }} onClick={handleSave}><Check size={14} /> Spremi</button>
                  <button style={{ ...outlineBtn, padding: '9px 14px', fontSize: 15, color: '#ac463b' }} onClick={handleDelete}><Trash2 size={14} /> Obriši</button>
                </>
              ) : isActive ? (
                <button style={{ ...smallBtn, padding: '10px 14px', fontSize: 15, flex: 1, justifyContent: 'center', background: '#1a2a4a', color: '#fff', border: 'none' }} onClick={handleClose}><Check size={14} /> Zatvori intervenciju</button>
              ) : (
                <button style={{ ...outlineBtn, padding: '9px 14px', fontSize: 15 }} onClick={handleReopen}><RotateCcw size={14} /> Ponovno otvori</button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ClosedRow({ intervention, user, onOpen, isFirst, hitLocation, incomplete }) {
  const d = new Date(intervention.started_at);
  const people = intervention.participants?.length || 0;
  const locs = (intervention.locations || []).filter(Boolean).length;
  const isParticipant = intervention.participants?.some(p => p.email === user.email);
  const isVolunteer = !isParticipant && intervention.volunteers?.some(v => v.email === user.email);
  const meta = [
    intervention.ended_at && formatSpan(new Date(intervention.ended_at) - d),
    `${people} ${plural(people, 'član', 'člana', 'članova')}`,
    locs > 0 && `${locs} ${plural(locs, 'lokacija', 'lokacije', 'lokacija')}`,
  ].filter(Boolean).join(' · ');
  return (
    <div onClick={onOpen} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '13px 14px', borderTop: isFirst ? 'none' : '1px solid #f1f3f6', cursor: 'pointer' }}>
      <div style={{ width: 48, textAlign: 'center', flexShrink: 0, borderRadius: 9, background: '#f2f4f7', padding: '5px 0', lineHeight: 1.05 }}>
        <div className="tab" style={{ fontSize: 20, fontWeight: 700, color: '#1a2130' }}>{d.getDate()}</div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#8a94a6', letterSpacing: '.06em', textTransform: 'uppercase' }}>{MONTHS_SHORT[d.getMonth()]}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 17.5, fontWeight: 700, color: '#1a2130', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{intervention.title}</div>
        <div className="tab" style={{ fontSize: 14.5, color: '#6b7587', marginTop: 1 }}>{meta}</div>
        {hitLocation && <div style={{ fontSize: 13.5, color: '#8a94a6', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}><MapPin size={12} style={{ flexShrink: 0 }} /><span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hitLocation}</span></div>}
        {incomplete && <div style={{ fontSize: 13, fontWeight: 700, color: '#a8781f', marginTop: 3 }}>Nepotpuno: {incomplete}</div>}
      </div>
      {isParticipant && <UserCheck size={17} color="#3f8f6b" strokeWidth={2.2} style={{ flexShrink: 0 }} />}
      {isVolunteer && <Hand size={17} color="#3a6ea3" strokeWidth={2.2} style={{ flexShrink: 0 }} />}
      <ChevronRight size={18} color="#b2bec3" style={{ flexShrink: 0 }} />
    </div>
  );
}

function Interventions({ user, openInterventionId, onInterventionOpened, menuOpen, initialParticipation = null, onResetInitialFilters, active, onFilterSummary, registerSearch, onSearchState }) {
  const [byYear, setByYear] = useState({});          // { godina: [intervencije] } — raspon može trebati više godina
  const [showCreate, setShowCreate] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [time, setTime] = useState('all');            // 'all' = godina iz Arhive; inače raspon unatrag
  const [custom, setCustom] = useState(defaultCustom);
  const [searchOpen, setSearchOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const searchRef = useRef(null);
  const [query, setQuery] = useState('');
  const [fInc, setFInc] = useState(false);            // voditelj: nepotpune zatvorene
  const [showFilters, setShowFilters] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterParticipation, setFilterParticipation] = useState(initialParticipation);
  const [closedVisible, setClosedVisible] = useState(CLOSED_PAGE_SIZE);
  const [openClosed, setOpenClosed] = useState(() => new Set(openInterventionId ? [openInterventionId] : []));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const filterTimerRef = useRef(null);

  const canManage = ['admin', 'manager'].includes(user.role);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => currentYear - i);
  const monthNames = ['Cijela godina', 'Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];
  const range = timeRange(time, true, custom);
  const neededYears = rangeYears(range, selectedYear);
  const yearsKey = neededYears.join(',');
  const interventions = useMemo(() => neededYears.flatMap(y => byYear[y] || []), [byYear, yearsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openFilters = () => {
    if (filterTimerRef.current) { clearTimeout(filterTimerRef.current); filterTimerRef.current = null; }
    setShowFilters(true);
    setFiltersVisible(true);
  };
  const closeFilters = () => {
    if (filterTimerRef.current) return; // zatvaranje već u tijeku
    setFiltersVisible(false);
    filterTimerRef.current = setTimeout(() => { setShowFilters(false); filterTimerRef.current = null; }, 260);
  };
  const toggleFilters = () => showFilters ? closeFilters() : openFilters();

  useEffect(() => { fetchInterventions(); }, [yearsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [focus, setFocus] = useState(null);          // { id, nonce } — koja kartica se otvara i skrola
  const refetchedForRef = useRef(null);
  useEffect(() => {
    if (!openInterventionId) return;
    const target = interventions.find(i => i.id === openInterventionId);
    if (!target) {
      // Stranica je možda mountana sa starim popisom (nova intervencija s Početne) — osvježi jednom
      if (refetchedForRef.current !== openInterventionId) { refetchedForRef.current = openInterventionId; fetchInterventions(); }
      else if (interventions.length > 0) onInterventionOpened && onInterventionOpened();
      return;
    }
    // Makni filtere koji bi je sakrili i učitaj dovoljno zatvorenih
    const hiddenByFilters = applyFilters([target]).length === 0
      || (target.status === 'active' && filterStatus === 'closed')
      || (target.status === 'closed' && filterStatus === 'active');
    if (hiddenByFilters) { setTime('all'); setQuery(''); setFInc(false); setFilterStatus('all'); setFilterParticipation(null); }
    if (target.status === 'closed') {
      setOpenClosed(prev => new Set(prev).add(target.id));
      const idx = interventions.filter(i => i.status === 'closed').sort((a, b) => new Date(b.started_at) - new Date(a.started_at)).findIndex(i => i.id === target.id);
      if (idx >= 0) setClosedVisible(v => Math.max(v, Math.ceil((idx + 1) / CLOSED_PAGE_SIZE) * CLOSED_PAGE_SIZE));
    }
    setFocus({ id: target.id, nonce: Date.now() });
    refetchedForRef.current = null;
    onInterventionOpened && onInterventionOpened();
  }, [openInterventionId, interventions]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleClosed = (id) => setOpenClosed(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  useEffect(() => {
    if (initialParticipation !== null) {
      setFilterParticipation(initialParticipation);
      onResetInitialFilters?.();
    }
  }, [initialParticipation]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInterventions = async () => {
    try {
      const res = await Promise.all(neededYears.map(y => api.get(`/interventions?year=${y}`)));
      setByYear(prev => { const next = { ...prev }; neededYears.forEach((y, i) => { next[y] = res[i].data; }); return next; });
    } catch (err) { console.error(err); }
  };

  const q = norm(query.trim());
  const locsOf = (i) => (i.locations || []).filter(Boolean);
  const searchText = (i) => norm([i.title, i.description, ...locsOf(i).flatMap(l => [l.name, l.address, l.description])].join(' '));
  // Lokacija koja je pogodila pretragu (prikaže se u retku, da se vidi zašto je intervencija tu)
  const hitLocation = (i) => {
    if (!q || norm(i.title).includes(q)) return null;
    const l = locsOf(i).find(x => norm([x.name, x.address, x.description].join(' ')).includes(q));
    return l ? (l.address && !norm(l.name).includes(q) ? `${l.name} · ${l.address}` : l.name) : null;
  };
  // Zatvorena, a izvještaju nešto fali
  const incompleteReason = (i) => {
    if (i.status !== 'closed') return null;
    const locs = locsOf(i);
    if (locs.length === 0) return 'nema lokacija';
    const noEnd = locs.filter(l => !l.ended_at).length;
    const noAfter = locs.filter(l => !(l.after_images > 0)).length;
    return [noEnd && `${noEnd === 1 ? 'lokacija' : `${noEnd} lokacije`} bez kraja rada`, noAfter && `${noAfter === 1 ? 'lokacija' : `${noAfter} lokacije`} bez fotografija „poslije”`].filter(Boolean).join(', ') || null;
  };
  const inTime = (i) => {
    const d = new Date(i.started_at);
    if (!range) return d.getFullYear() === selectedYear;
    return d >= range.from && d < range.to;
  };
  const passes = (i, o = {}) => {
    const part = o.part !== undefined ? o.part : filterParticipation;
    if (!inTime(i)) return false;
    if (part === 'participant' && !i.participants?.some(p => p.email === user.email)) return false;
    if (part === 'volunteer' && !(i.volunteers?.some(v => v.email === user.email) && !i.participants?.some(p => p.email === user.email))) return false;
    if ((o.inc ?? fInc) && !incompleteReason(i)) return false;
    if (q && !searchText(i).includes(q)) return false;
    return true;
  };
  const applyFilters = (list) => list.filter(i => passes(i));
  const countWith = (o) => interventions.filter(i => (filterStatus === 'all' || i.status === filterStatus) && passes(i, o)).length;

  const timeText = timeLabel(time, TIME_PAST, custom, selectedYear, currentYear);
  const timeOn = time !== 'all' || selectedYear !== currentYear;
  const activeItems = [
    ...(query.trim() ? [{ key: 'q', label: `„${query.trim()}”`, onRemove: () => { setQuery(''); setSearchOpen(false); } }] : []),
    ...(filterStatus !== 'all' ? [{ key: 'st', label: filterStatus === 'active' ? 'Aktivne' : 'Zatvorene', onRemove: () => setFilterStatus('all') }] : []),
    ...(filterParticipation === 'participant' ? [{ key: 'me', label: 'Sa mnom', onRemove: () => setFilterParticipation(null) }] : []),
    ...(filterParticipation === 'volunteer' ? [{ key: 'vol', label: 'Prijavio sam se', onRemove: () => setFilterParticipation(null) }] : []),
    ...(timeOn ? [{ key: 'time', label: timeText, onRemove: () => { setTime('all'); setSelectedYear(currentYear); } }] : []),
    ...(fInc ? [{ key: 'inc', label: 'Nepotpune', onRemove: () => setFInc(false) }] : []),
  ];
  const reasons = activeItems.map(a => a.label);
  const advCount = (filterStatus === 'closed' ? 1 : 0) + (filterParticipation === 'volunteer' ? 1 : 0) + (selectedYear !== currentYear ? 1 : 0);

  // Povećalo u navbaru (App.js): fokus u istoj gesti, pa tek onda otvori — iOS inače ne pokaže tipkovnicu
  const openSearch = () => {
    searchRef.current?.focus({ preventScroll: true });
    setSearchOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // Povećalo je prekidač: drugi dodir zatvara tipkovnicu i polje i briše tekst (isto kao ×)
  const toggleSearch = () => {
    if (searchOpen || query) { searchRef.current?.blur(); setQuery(''); setSearchOpen(false); }
    else openSearch();
  };
  const openSearchRef = useRef(toggleSearch);
  openSearchRef.current = toggleSearch;
  useEffect(() => {
    if (registerSearch) registerSearch('interventions', () => openSearchRef.current());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Navbar prikaže povećalo kao uključeno dok je pretraga otvorena
  const searchShown = searchOpen || !!query;
  useEffect(() => {
    if (onSearchState) onSearchState('interventions', searchShown);
  }, [searchShown]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalActiveFilters = reasons.length;
  const resetFilters = () => {
    setSelectedYear(currentYear); setTime('all'); setSearchOpen(false); setQuery(''); setFInc(false);
    setFilterStatus('all'); setFilterParticipation(null); onResetInitialFilters?.();
  };

  const activeInterventions = applyFilters(interventions.filter(i => i.status === 'active')).sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  const allClosed = applyFilters(interventions.filter(i => i.status === 'closed')).sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  const visibleClosed = allClosed.slice(0, closedVisible);
  const hasMoreClosed = allClosed.length > closedVisible;
  const activeCount = interventions.filter(i => i.status === 'active').length;
  const closedCount = interventions.filter(i => i.status === 'closed').length;

  const showActive = filterStatus !== 'closed';
  const showClosed = filterStatus !== 'active';
  const filteredCount = (showActive ? activeInterventions.length : 0) + (showClosed ? allClosed.length : 0);
  // Sažetak za stisnuti navbar
  const summaryText = totalActiveFilters ? `${reasons.slice(0, 2).join(' · ')}${reasons.length > 2 ? ` +${reasons.length - 2}` : ''} · ${filteredCount}` : null;
  useEffect(() => {
    if (onFilterSummary) onFilterSummary('interventions', summaryText);
  }, [summaryText]); // eslint-disable-line react-hooks/exhaustive-deps
  const statusFilters = [
    { key: 'all', label: 'Sve', count: activeCount + closedCount },
    { key: 'active', label: 'Aktivne', count: activeCount },
    { key: 'closed', label: 'Zatvorene', count: closedCount },
  ];

  const fabHidden = menuOpen || showCreate || showFilters;

  const mainButtons = [
    ...(canManage ? [{ icon: <Zap size={20} />, title: 'Aktiviraj intervenciju', onClick: () => setShowCreate(true) }] : []),
  ].reverse();

  const filterButton = {
    icon: <SlidersHorizontal size={20} />,
    title: 'Filteri',
    onClick: toggleFilters,
    active: showFilters,
    badge: totalActiveFilters,
  };

  return (
    <div>
      {/* ── ACTION BAR — samo desktop ── */}
      {!isMobile && (
        <div style={{ background: 'white', borderRadius: 12, marginBottom: 12, boxShadow: '0 1px 2px rgba(16,24,40,0.05), 0 4px 14px rgba(16,24,40,0.06)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '4px 6px', gap: 2 }}>
            {canManage && (
              <button onClick={() => setShowCreate(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#1a2a4a', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#f0f4fa'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <Zap size={15} color="#1a2a4a" /> Aktiviraj intervenciju
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={toggleSearch} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#636e72' }}>
              <Search size={15} /> Pretraži
            </button>
            <button onClick={toggleFilters} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', borderRadius: 8, background: totalActiveFilters > 0 ? '#e8f4fd' : 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: totalActiveFilters > 0 ? '#0984e3' : '#636e72', transition: 'background 0.15s' }} onMouseEnter={e => { if (!totalActiveFilters) e.currentTarget.style.background = '#f8f9fa'; }} onMouseLeave={e => { if (!totalActiveFilters) e.currentTarget.style.background = 'none'; }}>
              <SlidersHorizontal size={15} /> Filteri
              {totalActiveFilters > 0 && <span style={{ background: '#0984e3', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{totalActiveFilters}</span>}
              {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>
      )}

      {/* ── FILTER SHEET ── */}
      {showFilters && (
        <FilterSheet
          visible={filtersVisible}
          onClose={closeFilters}
          isMobile={isMobile}
          title="Filteri"
          totalActiveFilters={totalActiveFilters}
          resetFilters={resetFilters}
          resultCount={filteredCount}
          resultNoun={{ one: 'intervencija', few: 'intervencije', many: 'intervencija' }}
        >
          <div className="fb-wrap">
            <QuickChip icon={Zap} label="Aktivne" on={filterStatus === 'active'} count={interventions.filter(i => i.status === 'active' && passes(i)).length} onClick={() => setFilterStatus(s => (s === 'active' ? 'all' : 'active'))} />
            <QuickChip icon={User} label="Sa mnom" on={filterParticipation === 'participant'} count={countWith({ part: 'participant' })} onClick={() => setFilterParticipation(p => (p === 'participant' ? null : 'participant'))} />
            {canManage && <QuickChip icon={AlertTriangle} label="Nepotpune" on={fInc} count={countWith({ inc: true })} onClick={() => setFInc(v => !v)} />}
          </div>
          <SheetLabel>Vrijeme</SheetLabel>
          <TimeQuick list={TIME_PAST} time={time} setTime={setTime} custom={custom} setCustom={setCustom} />

          <AdvancedToggle open={advOpen || advCount > 0} onToggle={() => setAdvOpen(v => !(v || advCount > 0))} activeCount={advCount}>
            <SheetLabel>Status</SheetLabel>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {statusFilters.map(f => (
                <button key={f.key} onClick={() => setFilterStatus(f.key)} className={`filter-pill${filterStatus === f.key ? ' active' : ''}`}>
                  {f.label}<span style={{ marginLeft: 6, fontSize: 13, opacity: 0.7 }}>({f.count})</span>
                </button>
              ))}
            </div>
            <SheetLabel>Za mene</SheetLabel>
            <OptRow title="Prijavio sam se" sub="Javio si se, a nisi dodijeljen" on={filterParticipation === 'volunteer'} onClick={() => setFilterParticipation(p => (p === 'volunteer' ? null : 'volunteer'))} />
            <SheetLabel>Arhiva</SheetLabel>
            <ArchiveRow year={selectedYear} setYear={setSelectedYear} setTime={setTime} years={years} />
          </AdvancedToggle>
        </FilterSheet>
      )}

      <SearchField ref={searchRef} open={searchOpen} query={query} setQuery={setQuery}
        onClose={() => { setQuery(''); setSearchOpen(false); }} placeholder="Pretraži naziv ili adresu" />
      <ActiveStrip items={activeItems} count={filteredCount} noun={['intervencija', 'intervencije', 'intervencija']} onReset={resetFilters} />

      {showActive && activeInterventions.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7587', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, display: 'flex', gap: 8 }}>
            Aktivne <span style={{ color: '#9aa1ac', fontWeight: 600, letterSpacing: 0 }}>{activeInterventions.length}</span>
          </div>
          {activeInterventions.map(i => <InterventionCard key={i.id} intervention={i} user={user} onUpdate={fetchInterventions} defaultExpanded={i.id === openInterventionId} focusNonce={focus?.id === i.id ? focus.nonce : null} />)}
        </div>
      )}

      {showClosed && allClosed.length > 0 && (() => {
        // Zatvorene po mjesecima; otvorena intervencija "prekida" bijeli popis i prikazuje se kao puna kartica
        const monthKey = (i) => { const d = new Date(i.started_at); return `${d.getFullYear()}-${d.getMonth()}`; };
        const monthTotals = allClosed.reduce((acc, i) => { acc[monthKey(i)] = (acc[monthKey(i)] || 0) + 1; return acc; }, {});
        const groups = [];
        visibleClosed.forEach(i => {
          const k = monthKey(i);
          if (!groups.length || groups[groups.length - 1].key !== k) groups.push({ key: k, date: new Date(i.started_at), items: [] });
          groups[groups.length - 1].items.push(i);
        });
        const listBox = { background: '#fff', borderRadius: 14, overflow: 'hidden', marginBottom: 12, boxShadow: '0 1px 2px rgba(16,24,40,.05), 0 4px 14px rgba(16,24,40,.06)' };
        return (
          <div style={{ marginTop: (showActive && activeInterventions.length > 0) ? 18 : 0 }}>
            {groups.map(g => {
              const blocks = [];
              let run = [];
              const flush = () => { if (run.length) { const rows = run; blocks.push(<div key={`r${rows[0].id}`} style={listBox}>{rows.map((i, idx) => <ClosedRow key={i.id} intervention={i} user={user} isFirst={idx === 0} hitLocation={hitLocation(i)} incomplete={fInc ? incompleteReason(i) : null} onOpen={() => toggleClosed(i.id)} />)}</div>); run = []; } };
              g.items.forEach(i => {
                if (openClosed.has(i.id)) { flush(); blocks.push(<InterventionCard key={i.id} intervention={i} user={user} onUpdate={fetchInterventions} defaultExpanded onCollapse={() => toggleClosed(i.id)} focusNonce={focus?.id === i.id ? focus.nonce : null} />); }
                else run.push(i);
              });
              flush();
              const total = monthTotals[g.key];
              return (
                <div key={g.key}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7587', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 0 8px', display: 'flex', gap: 8 }}>
                    {monthNames[g.date.getMonth() + 1]} {g.date.getFullYear()}.
                    <span style={{ marginLeft: 'auto', color: '#9aa1ac', fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>{total} {total === 1 ? 'zatvorena' : 'zatvorenih'}</span>
                  </div>
                  {blocks}
                </div>
              );
            })}
            {hasMoreClosed && (
              <button onClick={() => setClosedVisible(v => v + CLOSED_PAGE_SIZE)} style={{ width: '100%', padding: '12px 16px', marginTop: 2, background: 'white', border: 'none', borderRadius: 14, cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#1a2a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 1px 2px rgba(16,24,40,.05), 0 4px 14px rgba(16,24,40,.06)' }}>
                <ChevronDown size={15} /> Učitaj još ({allClosed.length - closedVisible} preostalo)
              </button>
            )}
          </div>
        );
      })()}

      {filteredCount === 0 && (
        reasons.length > 0
          ? <EmptyFiltered noun="intervencija" reasons={reasons} onReset={resetFilters} />
          : <div className="fb-empty"><b>Nema intervencija</b></div>
      )}

      {/* ── FAB Stack — samo mobile ── */}
      {isMobile && (
        <FabStack
          mainButtons={mainButtons}
          filterButton={filterButton}
          hasActiveFilters={totalActiveFilters > 0}
          onClearFilters={resetFilters}
          hidden={fabHidden}
          active={active}
        />
      )}

      {showCreate && <ActivateInterventionModal onClose={() => setShowCreate(false)} onSaved={() => { fetchInterventions(); setShowCreate(false); }} />}
    </div>
  );
}

export default Interventions;
